import {
  IFillStyle,
  IKuikaStyle,
  IStylingPosition,
  IStylingTypography,
  QuickStyleKey,
  TextAlignTypes
} from "@kuika/kuika-cl-model";
import {
  isUsableStyleId,
  styleIdEquals,
  toFormStyleIds
} from "../../../right-panel/styling-panel/unified-styling-selector/style-tag-helpers";

/**
 * Pure read/patch rules for the selection bar's quick styles. Writes are field-level patches of the
 * component's own styleObj — an inline override on top of whatever style tag it carries, exactly what
 * the styling panel produces for the same edit. (The MCP `setDesignerComponentStyles` path is NOT used:
 * it detaches the section's tag whenever the section has inline values, has no `fontStyle`, and commits
 * with a fixed change reason.)
 */

/** The collections a tag's values are resolved from when the component has no inline value. */
export interface IQuickStyleProject {
  stylingTypography?: IStylingTypography[];
  stylingFill?: IFillStyle[];
}

export interface ITextFormat {
  bold: boolean;
  italic: boolean;
  underline: boolean;
}

export type TextFormatKey = keyof ITextFormat;

export type TextAlignValue = "left" | "center" | "right";

/** The first tag id in a section's chain; empty and detached slots (`""` / `null`) are skipped. */
const firstId = (joined: string | null | undefined, ids: string[] | null | undefined): string | undefined => {
  if (ids && ids.length > 0) {
    return ids.find(isUsableStyleId);
  }
  if (typeof joined === "string") {
    return joined.split("|").find(isUsableStyleId);
  }
  return undefined;
};

/** The typography the component shows: its inline values over the first applied tag's. */
export function resolveTypography(style: IKuikaStyle | undefined, project: IQuickStyleProject): IStylingTypography {
  const inline: IStylingTypography = style?.text ?? ({} as IStylingTypography);
  const id: string | undefined = firstId(inline.typoId, inline.typoIds);
  const fromTag: IStylingTypography | undefined = id
    ? project.stylingTypography?.find((t: IStylingTypography): boolean => styleIdEquals(t.typoId, id))
    : undefined;
  return { ...(fromTag ?? {}), ...stripNullish(inline) } as IStylingTypography;
}

export function resolveFillColor(style: IKuikaStyle | undefined, project: IQuickStyleProject): string | null {
  const inline: IFillStyle | undefined = style?.fill;
  if (inline?.color) {
    return inline.color;
  }
  const id: string | undefined = firstId(inline?.fillId, inline?.fillIds);
  const fromTag: IFillStyle | undefined = id
    ? project.stylingFill?.find((f: IFillStyle): boolean => styleIdEquals(f.fillId, id))
    : undefined;
  return fromTag?.color ?? null;
}

function stripNullish<T extends object>(value: T): Partial<T> {
  const out: Partial<T> = {};
  (Object.keys(value) as (keyof T)[]).forEach((key: keyof T): void => {
    if (value[key] !== null && value[key] !== undefined) {
      out[key] = value[key];
    }
  });
  return out;
}

export function readTextFormat(typography: IStylingTypography): ITextFormat {
  return {
    bold: (typography.fontWeight ?? 400) >= 600,
    italic: typography.fontStyle === "italic",
    underline: decorationTokens(typography.textDecoration).includes(UNDERLINE_TOKEN)
  };
}

const UNDERLINE_TOKEN: string = "underline";
/** CSS `text-decoration` value that means "no decoration line". */
const NO_DECORATION: string = "none";

/** The line tokens of a `text-decoration` value ("underline line-through" → both); "none" has none. */
function decorationTokens(value: string | undefined): string[] {
  if (typeof value !== "string") {
    return [];
  }
  return value.split(/\s+/).filter((token: string): boolean => token !== "" && token !== NO_DECORATION);
}

/**
 * Adds or removes one line token while keeping the others ("line-through", "overline", a colour or
 * style keyword) — the bar's Underline toggle must not wipe a strike-through set in the styling panel.
 */
export function toggleDecorationToken(value: string | undefined, token: string, isOn: boolean): string {
  const others: string[] = decorationTokens(value).filter((t: string): boolean => t !== token);
  const next: string[] = isOn ? [...others, token] : others;
  return next.length > 0 ? next.join(" ") : NO_DECORATION;
}

const ALIGN_TO_ENUM: Record<TextAlignValue, TextAlignTypes> = {
  left: TextAlignTypes.Left,
  center: TextAlignTypes.Center,
  right: TextAlignTypes.Right
};

export function readTextAlign(style: IKuikaStyle | undefined): TextAlignValue | null {
  switch (style?.position?.textAlign) {
    case TextAlignTypes.Left:
      return "left";
    case TextAlignTypes.Center:
      return "center";
    case TextAlignTypes.Right:
      return "right";
    default:
      return null;
  }
}

/** One quick-style edit. */
export type QuickStylePatch =
  | IFontSizePatch
  | IFontFamilyPatch
  | ITextColorPatch
  | ITextFormatPatch
  | IFillColorPatch
  | ITextAlignPatch;

export interface IFontSizePatch {
  key: "text.fontSize";
  value: number;
}
export interface IFontFamilyPatch {
  key: "text.fontFamily";
  value: string;
}
export interface ITextColorPatch {
  key: "text.color";
  value: string;
}
export interface ITextFormatPatch {
  key: "text.format";
  format: TextFormatKey;
  isOn: boolean;
}
export interface IFillColorPatch {
  key: "fill.color";
  value: string;
}
export interface ITextAlignPatch {
  key: "position.textAlign";
  value: TextAlignValue;
}

/**
 * The styleObj to commit for one quick-style edit: a FORM-shaped deep copy (tag ids hydrated — see
 * `toFormStyleIds`) with only the edited fields changed. Never mutates `current`.
 */
export function buildQuickStyleObj(current: IKuikaStyle | undefined, patch: QuickStylePatch): IKuikaStyle {
  const next: IKuikaStyle = toFormStyleIds(current);
  const text = (): IStylingTypography => {
    next.text = next.text ?? ({} as IStylingTypography);
    return next.text;
  };
  switch (patch.key) {
    case "text.fontSize": {
      const t: IStylingTypography = text();
      t.fontSize = patch.value;
      t.fontSizeStyle = t.fontSizeStyle || "px";
      break;
    }
    case "text.fontFamily":
      text().fontFamily = patch.value;
      break;
    case "text.color":
      text().color = patch.value;
      break;
    case "text.format": {
      const t: IStylingTypography = text();
      if (patch.format === "bold") {
        t.fontWeight = patch.isOn ? 700 : 400;
      } else if (patch.format === "italic") {
        t.fontStyle = patch.isOn ? "italic" : "normal";
      } else {
        t.textDecoration = toggleDecorationToken(t.textDecoration, UNDERLINE_TOKEN, patch.isOn);
      }
      break;
    }
    case "fill.color":
      next.fill = { ...(next.fill ?? ({} as IFillStyle)), color: patch.value };
      break;
    case "position.textAlign":
      next.position = { ...(next.position ?? ({} as IStylingPosition)), textAlign: ALIGN_TO_ENUM[patch.value] };
      break;
    default: {
      const unhandled: never = patch;
      throw new Error(`Unhandled quick style patch: ${JSON.stringify(unhandled)}`);
    }
  }
  return next;
}

/** Stable test-id / label per style key. */
export const STYLE_LABELS: Record<QuickStyleKey, string> = {
  "text.fontSize": "Font size",
  "text.fontFamily": "Font",
  "text.color": "Text color",
  "text.format": "Text format",
  "fill.color": "Fill color",
  "position.textAlign": "Text alignment"
};
